import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, Clock, AlertCircle, Wrench } from 'lucide-react';
import { getMaintenanceStatus, formatDaysUntil } from '../../../../utils/maintenanceScheduling';

/**
 * Equipment Quick Stats Component
 * Displays 3 at-a-glance stat cards for equipment:
 * 1. Compliance Status (overall equipment compliance)
 * 2. Next Service Due (shows which service type and when)
 * 3. Linked Defects (count of open defects)
 */
const EquipmentQuickStats = ({
  equipment,
  maintenanceHistory = [],
  defects = [],
  linkedJobs = [],
  onNavigate
}) => {

  // Calculate maintenance status using the new per-item logic
  const maintenanceStatus = useMemo(() => {
    return getMaintenanceStatus(equipment, linkedJobs, maintenanceHistory);
  }, [equipment, linkedJobs, maintenanceHistory]);

  // Extract compliance status from maintenance status
  const complianceStatus = useMemo(() => {
    const status = maintenanceStatus.status;
    const display = maintenanceStatus.display;
    
    let icon = CheckCircle;
    if (status === 'overdue' || status === 'never_serviced') {
      icon = AlertCircle;
    } else if (status === 'due_this_week' || status === 'due_this_month') {
      icon = AlertTriangle;
    } else if (status === 'unknown' || status === 'unscheduled') {
      icon = Clock;
    }
    
    return {
      status,
      label: display.label,
      color: display.color,
      icon,
      iconColor: ['ok', 'never_serviced', 'unscheduled', 'unknown'].includes(status)
        ? 'text-cyan-600'
        : display.iconColor
    };
  }, [maintenanceStatus]);

  // Format next service due information
  const nextServiceDue = useMemo(() => {
    const { type, daysUntil, urgent, overdue } = maintenanceStatus;
    
    return {
      type: type || 'Unknown',
      timeframe: formatDaysUntil(daysUntil, overdue),
      urgent: urgent || false
    };
  }, [maintenanceStatus]);

  // Count open defects only (not total historical)
  const defectsCount = useMemo(() => {
    // Filter for open/active defects only - excluding resolved, closed, fixed, completed
    return defects.filter(d => {
      const status = (d.status || '').toLowerCase();
      return status !== 'resolved' && 
             status !== 'closed' && 
             status !== 'fixed' && 
             status !== 'completed' &&
             status !== 'repaired';
    }).length;
  }, [defects]);

  const ComplianceIcon = complianceStatus.icon;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* Compliance Status Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <ComplianceIcon className={`h-6 w-6 ${complianceStatus.iconColor}`} />
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${complianceStatus.color}`}>
            {complianceStatus.label}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">Compliance</p>
      </div>

      {/* Next Service Due Card */}
      <div
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate && onNavigate('history')}
      >
        <div className="flex items-center justify-between mb-2">
          <Wrench className={`h-6 w-6 ${nextServiceDue.urgent ? 'text-amber-600' : 'text-cyan-600'}`} />
          {nextServiceDue.urgent && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">
              Urgent
            </span>
          )}
        </div>
        <p className="text-base font-bold text-gray-900 truncate" title={nextServiceDue.type}>
          {nextServiceDue.type}
        </p>
        <p className="text-xs text-gray-500 mt-1">{nextServiceDue.timeframe}</p>
      </div>

      {/* Linked Defects Card */}
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${
          defectsCount > 0 ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
        }`}
        onClick={() => defectsCount > 0 && onNavigate && onNavigate('defects')}
      >
        <div className="flex items-center justify-between mb-2">
          <AlertTriangle className={`h-6 w-6 ${defectsCount > 0 ? 'text-red-600' : 'text-cyan-600'}`} />
          {defectsCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-100 text-red-800">
              Open
            </span>
          )}
        </div>
        <p className="text-base font-bold text-gray-900">{defectsCount}</p>
        <p className="text-xs text-gray-500 mt-1">Linked Defects</p>
      </div>
    </div>
  );
};

export default EquipmentQuickStats;
